'use client';

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePostHog } from 'posthog-js/react';
import { useSuspenseQuery, useQuery } from '@tanstack/react-query';

import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import {
  CampaignUrl,
  CampaignUrlRef,
  CampaignBusinessDescription,
  CampaignBusinessDescriptionRef,
} from '@/components/campaign/new';
import { CampaignBusinessCustomer } from '@/components/campaign/onboarding/campaign-business-customer';
import {
  CampaignBusinessProduct,
  CampaignBusinessProductRef,
} from '@/components/campaign/onboarding/campaign-business-product';
import { useCreateBusinessMutation, useUpdateBusinessMutation, useGenerateBrandIntelMutation, useOrganizationCurrencyMutation, userProfileQueryOptions, businessQueryOptions } from '@/lib/query-options';
import { updateOrganizationBudget } from '@/lib/apis';
import { useToast } from '@/hooks/useToast';
import { useAtomValue, useSetAtom } from 'jotai';
import { organizationDataAtom, manualBrandEntryAtom } from '@/store';
import { useQueryClient } from '@tanstack/react-query';
import {
  isGenericEmailProvider,
  getEmailDomain,
  getBusinessNameFromDomain,
  getWebsiteFromDomain,
} from '@/lib/utils';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

import { CURRENCY_OPTIONS } from '@/lib/currencies';

export const Route = createFileRoute('/_authenticated/new/_layout/brand')({
  component: withOrgReset(BrandCreate),
});

// Steps for brand creation - merged URL and brand name into one step
enum BRAND_CREATION_STEP {
  BUSINESS_INFO = 'BUSINESS_INFO',
  SET_BUDGET = 'SET_BUDGET',
  ADJUST_BUSINESS_DESCRIPTION = 'ADJUST_BUSINESS_DESCRIPTION',
  BUSINESS_IDEAL_CUSTOMER = 'BUSINESS_IDEAL_CUSTOMER',
  MAIN_BUSINESS_PRODUCTS = 'MAIN_BUSINESS_PRODUCTS',
}

const STEP_CONFIG: Record<
  BRAND_CREATION_STEP,
  {
    stepIndicator: number;
    title: string;
    backStep: BRAND_CREATION_STEP | null;
  }
> = {
  [BRAND_CREATION_STEP.BUSINESS_INFO]: {
    stepIndicator: 0,
    title: 'Enter Your Business Details',
    backStep: null,
  },
  [BRAND_CREATION_STEP.SET_BUDGET]: {
    stepIndicator: 1,
    title: 'Set Your Daily Budget',
    backStep: BRAND_CREATION_STEP.BUSINESS_INFO,
  },
  [BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION]: {
    stepIndicator: 2,
    title: 'Review Website Summary',
    backStep: BRAND_CREATION_STEP.SET_BUDGET,
  },
  [BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER]: {
    stepIndicator: 3,
    title: 'Who Are Your Customers?',
    backStep: BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION,
  },
  [BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS]: {
    stepIndicator: 4,
    title: 'What Do You Sell?',
    backStep: BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER,
  },
};

type BrandData = {
  businessUrl: string;
  businessDescription: string;
  brandName: string;
  idealCustomer: string;
  products: string[];
  budget: number;
  currency: string;
};

function BrandCreate() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const { currency: detectedCurrency } = useCurrencySymbol();
  const organizationData = useAtomValue(organizationDataAtom);
  const isManualEntry = useAtomValue(manualBrandEntryAtom);
  const setManualBrandEntry = useSetAtom(manualBrandEntryAtom);
  const { data: userProfile } = useSuspenseQuery(userProfileQueryOptions());

  // Check if user has a business email domain (not Gmail, Hotmail, etc.)
  const emailDomainInfo = useMemo(() => {
    const email = userProfile?.email;
    if (!email || isGenericEmailProvider(email)) {
      return null;
    }
    const domain = getEmailDomain(email);
    if (!domain) return null;
    return {
      domain,
      suggestedBusinessName: getBusinessNameFromDomain(domain),
      suggestedWebsite: getWebsiteFromDomain(domain),
    };
  }, [userProfile?.email]);

  // Priority for brand name: 1) Organization name (user explicitly set this), 2) Email domain as fallback
  const defaultBrandName = organizationData?.name || emailDomainInfo?.suggestedBusinessName || '';
  // Priority for URL: 1) Organization name if it looks like a domain, 2) Email domain suggestion, 3) Empty
  const defaultBusinessUrl = organizationData?.name?.includes('.')
    ? `https://${organizationData.name.replace(/^https?:\/\//, '')}`
    : emailDomainInfo?.suggestedWebsite || 'https://';

  const [brandData, setBrandData] = useState<BrandData>(() => ({
    businessUrl: defaultBusinessUrl,
    businessDescription: '',
    brandName: defaultBrandName,
    idealCustomer: '',
    products: [],
    budget: 50,
    currency: detectedCurrency ?? 'EUR',
  }));

  // Track if we pre-populated from organization name or email domain (to potentially skip screens)
  const [wasPrePopulated] = useState(() => !!organizationData?.name || !!emailDomainInfo);

  const [currentStep, setCurrentStep] = useState(BRAND_CREATION_STEP.BUSINESS_INFO);
  // Tell-on-click validation for the BUSINESS_INFO step (#5857): Continue stays enabled; clicking
  // with missing/invalid typed input marks the offending field(s) in the child step component.
  const [businessInfoErrors, setBusinessInfoErrors] = useState<{ brandName?: string; businessUrl?: string }>({});
  const [stepValidation, setStepValidation] = useState<Record<BRAND_CREATION_STEP, boolean>>({
    [BRAND_CREATION_STEP.BUSINESS_INFO]: false,
    [BRAND_CREATION_STEP.SET_BUDGET]: false,
    [BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION]: false,
    [BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER]: false,
    [BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS]: false,
  });

  // Memoized callbacks to prevent infinite re-render loops in child components
  const setBusinessInfoStepValid = useCallback((valid: boolean) => {
    setStepValidation((prev) => ({ ...prev, [BRAND_CREATION_STEP.BUSINESS_INFO]: valid }));
  }, []);

  const setDescriptionStepValid = useCallback((valid: boolean) => {
    setStepValidation((prev) => ({ ...prev, [BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION]: valid }));
  }, []);

  const setCustomerStepValid = useCallback((valid: boolean) => {
    setStepValidation((prev) => ({ ...prev, [BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER]: valid }));
  }, []);

  const setProductStepValid = useCallback((valid: boolean) => {
    setStepValidation((prev) => ({ ...prev, [BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS]: valid }));
  }, []);

  const stepStartTimeRef = useRef<number>(Date.now());
  const campaignUrlRef = useRef<CampaignUrlRef>(null);
  const campaignBusinessDescriptionRef = useRef<CampaignBusinessDescriptionRef>(null);
  const campaignBusinessProductRef = useRef<CampaignBusinessProductRef>(null);

  const createBusinessMutation = useCreateBusinessMutation();
  const updateBusinessMutation = useUpdateBusinessMutation(organizationData?.id || 0);
  const generateBrandIntelMutation = useGenerateBrandIntelMutation(organizationData?.id || 0);
  const currencyMutation = useOrganizationCurrencyMutation();
  const setOrganizationData = useSetAtom(organizationDataAtom);

  // Check if an existing business already exists for this organization
  const { data: existingBusiness } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Pre-populate from existing business if available
  useEffect(() => {
    if (existingBusiness) {
      setBrandData(prev => ({
        ...prev,
        brandName: existingBusiness.brand_name || prev.brandName,
        businessUrl: existingBusiness.business_url || prev.businessUrl,
      }));
    }
  }, [existingBusiness?.id]);

  // Reset manual entry atom on unmount
  useEffect(() => () => setManualBrandEntry(false), [setManualBrandEntry]);

  // Track brand creation start
  useEffect(() => {
    posthog?.capture('brand_creation_started', {
      pre_populated: wasPrePopulated,
      pre_populated_source: emailDomainInfo ? 'email_domain' : (organizationData?.name ? 'organization' : 'none'),
      suggested_business_name: defaultBrandName,
      suggested_website: emailDomainInfo?.suggestedWebsite,
    });

    return () => {
      posthog?.capture('brand_creation_abandoned', {
        last_step: STEP_CONFIG[currentStep].title,
        progress_percentage: Math.round(
          (STEP_CONFIG[currentStep].stepIndicator / Object.keys(STEP_CONFIG).length) * 100
        ),
      });
    };
  }, []);

  // Validate budget step when it becomes active
  useEffect(() => {
    if (currentStep === BRAND_CREATION_STEP.SET_BUDGET) {
      setStepValidation((prev) => ({ ...prev, [BRAND_CREATION_STEP.SET_BUDGET]: brandData.budget > 0 && !!brandData.currency }));
    }
  }, [currentStep, brandData.budget, brandData.currency]);

  // Track step changes
  useEffect(() => {
    stepStartTimeRef.current = Date.now();
    posthog?.capture('brand_creation_step_viewed', {
      step: STEP_CONFIG[currentStep].title,
      step_index: STEP_CONFIG[currentStep].stepIndicator,
    });
  }, [currentStep]);

  const updateBrandData = <K extends keyof BrandData>(field: K, value: BrandData[K]) => {
    setBrandData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const trackStepCompletion = (
    prevStep: BRAND_CREATION_STEP,
    nextStep: BRAND_CREATION_STEP
  ) => {
    const timeSpentMs = Date.now() - stepStartTimeRef.current;

    posthog?.capture('brand_creation_step_completed', {
      step_completed: STEP_CONFIG[prevStep].title,
      next_step: STEP_CONFIG[nextStep].title,
      time_spent_seconds: Math.round(timeSpentMs / 1000),
    });

    setCurrentStep(nextStep);
  };

  const handleSubmit = async () => {
    if (!organizationData?.id) {
      toast({
        title: 'Error',
        description: 'Organization not found. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }

    posthog?.capture('brand_creation_submission_started', {
      has_products: brandData.products.length > 0,
      has_ideal_customer: Boolean(brandData.idealCustomer),
      is_update: !!existingBusiness,
    });

    try {
      if (existingBusiness && isManualEntry) {
        // Manual entry mode: save all user-provided data directly without
        // re-triggering brand intel (which already failed for this business).
        await updateBusinessMutation.mutateAsync({
          businessId: existingBusiness.id,
          data: {
            business_url: brandData.businessUrl,
            brand_name: brandData.brandName,
            business_desc: brandData.businessDescription,
          },
        });

        posthog?.capture('brand_manual_entry_completed', {
          brand_name: brandData.brandName,
          business_id: existingBusiness.id,
          description_length: brandData.businessDescription.length,
        });
      } else if (existingBusiness) {
        // Only business_url and brand_name are sent here. Fields like businessDescription,
        // idealCustomer, and products are intentionally omitted because they are regenerated
        // by the generateBrandIntel mutation below, which re-scrapes and re-analyzes the site.
        await updateBusinessMutation.mutateAsync({
          businessId: existingBusiness.id,
          data: {
            business_url: brandData.businessUrl,
            brand_name: brandData.brandName,
          },
        });

        // Trigger brand intel regeneration
        await generateBrandIntelMutation.mutateAsync(existingBusiness.id);

        posthog?.capture('brand_profile_updated', {
          brand_name: brandData.brandName,
          business_id: existingBusiness.id,
        });
      } else {
        // Create new business
        await createBusinessMutation.mutateAsync({
          business: {
            business_url: brandData.businessUrl,
            business_desc: brandData.businessDescription,
            brand_name: brandData.brandName,
            ideal_customer: brandData.idealCustomer,
            products: brandData.products,
            competitors: [],
            target_locations: [],
            budget: brandData.budget,
            ad_pages: [],
          },
          organizationId: organizationData.id,
        });

        posthog?.capture('brand_creation_completed', {
          brand_name: brandData.brandName,
          product_count: brandData.products.length,
        });
      }

      // Save max_spend and default_currency on the organization
      if (brandData.budget > 0 && organizationData) {
        const maxSpend = brandData.budget * 30;
        // #5656: max_spend must go through the admin-gated budget endpoint, which throws on failure.
        // The full-org PUT is owner-only and silently 403'd non-owner admins, so their budget never
        // persisted (while the Google platform budget did, from the backend). The currency has its own
        // owner-or-admin endpoint too (#6828).
        await updateOrganizationBudget(organizationData.id, maxSpend);
        // The budget is already persisted and cannot be rolled back, so a refused currency does not fail
        // the step: it says why, and the organization keeps no currency, which the Organizations page then
        // asks for, rather than one this page only pretended to save.
        let savedCurrency = organizationData.default_currency;
        try {
          await currencyMutation.mutateAsync({ organizationId: organizationData.id, currency: brandData.currency });
          savedCurrency = brandData.currency;
        } catch (currencyError) {
          toast({
            description: currencyError instanceof Error ? currencyError.message : 'Failed to update currency',
            variant: 'destructive',
          });
        }
        setOrganizationData({ ...organizationData, max_spend: maxSpend, default_currency: savedCurrency });
      }

      // Query invalidation for updateBusiness and generateBrandIntel is handled
      // by their respective mutation onSuccess callbacks in query-options.ts.
      // Only need to invalidate for the createBusiness path.
      if (!existingBusiness) {
        await queryClient.invalidateQueries({ queryKey: ['organizations', organizationData.id, 'business'] });
        await queryClient.invalidateQueries({ queryKey: ['businessTaskTracking'] });
      }

      navigate({ to: '/business' });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save brand';
      posthog?.capture('brand_creation_failed', {
        error_message: errorMessage,
        is_update: !!existingBusiness,
      });

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleBack = () => {
    if (currentStep === BRAND_CREATION_STEP.BUSINESS_INFO) {
      navigate({ to: '/business' });
      return;
    }

    // If pre-populated and on customer step, go back to business info (skip description)
    let backStep = STEP_CONFIG[currentStep].backStep;
    if (isManualEntry && currentStep === BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION) {
      backStep = BRAND_CREATION_STEP.BUSINESS_INFO;
    } else if (wasPrePopulated && currentStep === BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER) {
      backStep = BRAND_CREATION_STEP.BUSINESS_INFO;
    }

    if (backStep) {
      posthog?.capture('brand_creation_back_button_clicked', {
        from_step: STEP_CONFIG[currentStep].title,
        to_step: STEP_CONFIG[backStep].title,
      });
      trackStepCompletion(currentStep, backStep);
    }
  };

  const handleNext = async () => {
    const stepValues = Object.values(BRAND_CREATION_STEP) as BRAND_CREATION_STEP[];
    const currentIndex = stepValues.indexOf(currentStep);
    const nextStep = stepValues[currentIndex + 1];

    switch (currentStep) {
      case BRAND_CREATION_STEP.BUSINESS_INFO: {
        // Tell-on-click (#5857): mark each missing/invalid field instead of silently returning.
        const errors: typeof businessInfoErrors = {};
        if (!brandData.brandName?.trim()) {
          errors.brandName = 'Required';
        } else if (brandData.brandName.trim().length < 2) {
          errors.brandName = 'At least 2 characters';
        }
        if (!brandData.businessUrl || brandData.businessUrl === 'https://') {
          errors.businessUrl = 'Required';
        }
        if (errors.brandName || errors.businessUrl) {
          setBusinessInfoErrors(errors);
          return;
        }
        if (isManualEntry) {
          // Manual entry: let the user enter a description before saving
          trackStepCompletion(currentStep, BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION);
        } else {
          // Always show budget step
          trackStepCompletion(currentStep, BRAND_CREATION_STEP.SET_BUDGET);
        }
        break;
      }
      case BRAND_CREATION_STEP.SET_BUDGET: {
        await handleSubmit();
        break;
      }
      case BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION: {
        // approveDescription syncs the internal description state back to brandData
        const success = await campaignBusinessDescriptionRef.current?.approveDescription();
        if (success) {
          if (isManualEntry) {
            await handleSubmit();
          } else {
            trackStepCompletion(currentStep, BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER);
          }
        }
        break;
      }
      case BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER: {
        if (brandData.idealCustomer) {
          trackStepCompletion(currentStep, BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS);
        }
        break;
      }
      case BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS: {
        const success = campaignBusinessProductRef.current?.approveProducts();
        if (success) {
          await handleSubmit();
        }
        break;
      }
      default:
        if (nextStep !== undefined) {
          trackStepCompletion(currentStep, nextStep);
        }
    }
  };

  const totalSteps = Object.keys(STEP_CONFIG).length;

  // Calculate effective total steps
  const effectiveTotalSteps = isManualEntry
    ? 2 // BUSINESS_INFO + ADJUST_BUSINESS_DESCRIPTION
    : wasPrePopulated
      ? 2 // BUSINESS_INFO + SET_BUDGET
      : totalSteps;
  const effectiveCurrentStep = useMemo(() => {
    if (isManualEntry) {
      return currentStep === BRAND_CREATION_STEP.BUSINESS_INFO ? 0 : 1;
    }
    if (wasPrePopulated) {
      return currentStep === BRAND_CREATION_STEP.BUSINESS_INFO ? 0 : 1;
    }
    return STEP_CONFIG[currentStep].stepIndicator;
  }, [currentStep, wasPrePopulated, isManualEntry]);

  return (
    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
      <StepsIndicator
        currentStep={effectiveCurrentStep + 1}
        totalSteps={effectiveTotalSteps}
      />
      <div className="flex justify-center">
        <div className="w-[60%]">
          <h1 className="text-3xl font-bold text-center mb-8">
            {isManualEntry && currentStep === BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION
              ? 'Describe Your Business'
              : STEP_CONFIG[currentStep].title}
          </h1>
          <Card className="p-6">
            {currentStep === BRAND_CREATION_STEP.BUSINESS_INFO && (
              <CampaignUrl
                ref={campaignUrlRef}
                title=""
                subTitle=""
                loadingTitle="Analyzing Your Website"
                loadingSubTitle="Generating business insights..."
                onNext={() => handleSubmit()}
                businessUrl={brandData.businessUrl}
                setBusinessUrl={(url) => {
                  updateBrandData('businessUrl', url);
                  // Tell-on-click (#5857): the field's error clears as soon as it is edited.
                  setBusinessInfoErrors((prev) => (prev.businessUrl ? { ...prev, businessUrl: undefined } : prev));
                }}
                setBusinessDescription={(description) => updateBrandData('businessDescription', description)}
                setBusinessName={(name) => updateBrandData('brandName', name)}
                setStepValid={setBusinessInfoStepValid}
                showInternalContinueButton={false}
                brandName={brandData.brandName}
                setBrandName={(name) => {
                  updateBrandData('brandName', name);
                  setBusinessInfoErrors((prev) => (prev.brandName ? { ...prev, brandName: undefined } : prev));
                }}
                showBrandNameInput={true}
                fieldErrors={businessInfoErrors}
              />
            )}

            {currentStep === BRAND_CREATION_STEP.SET_BUDGET && (
              <div className="space-y-6">
                <p className="text-sm text-gray-700">How much do you want to spend per day on advertising?</p>
                <div className="flex items-center gap-3 max-w-xs">
                  <Select
                    value={brandData.currency || undefined}
                    onValueChange={(value) => {
                      updateBrandData('currency', value);
                      setStepValidation((prev) => ({ ...prev, [BRAND_CREATION_STEP.SET_BUDGET]: !!value && brandData.budget > 0 }));
                    }}
                  >
                    <SelectTrigger className="w-[120px] h-9 shrink-0">
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min={1}
                    value={brandData.budget || ''}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      updateBrandData('budget', value);
                      setStepValidation((prev) => ({ ...prev, [BRAND_CREATION_STEP.SET_BUDGET]: value > 0 && !!brandData.currency }));
                    }}
                    placeholder="50"
                    className="w-[120px] h-9"
                  />
                  <span className="text-sm text-gray-500">/ day</span>
                </div>
                <p className="text-xs text-gray-500">This can be changed at any time from your budget settings.</p>
                <p className="text-xs text-gray-500">
                  Split this between Google Ads and Meta Ads later from Settings → Budget.
                </p>
              </div>
            )}

            {currentStep === BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION && (isManualEntry || !wasPrePopulated) && (
              <CampaignBusinessDescription
                ref={campaignBusinessDescriptionRef}
                title=""
                businessDescription={brandData.businessDescription}
                businessUrl={brandData.businessUrl}
                onNext={() => trackStepCompletion(currentStep, BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER)}
                setBusinessDescription={(description) => updateBrandData('businessDescription', description)}
                setStepValid={setDescriptionStepValid}
                showInternalContinueButton={false}
              />
            )}

            {currentStep === BRAND_CREATION_STEP.BUSINESS_IDEAL_CUSTOMER && (
              <CampaignBusinessCustomer
                businessUrl={brandData.businessUrl}
                onNext={() => trackStepCompletion(currentStep, BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS)}
                setBusinessCustomer={(customer) => updateBrandData('idealCustomer', customer)}
                setStepValid={setCustomerStepValid}
              />
            )}

            {currentStep === BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS && (
              <CampaignBusinessProduct
                ref={campaignBusinessProductRef}
                businessUrl={brandData.businessUrl}
                businessProducts={brandData.products}
                onNext={handleSubmit}
                setBusinessProducts={(products) => updateBrandData('products', products)}
                setStepValid={setProductStepValid}
              />
            )}
          </Card>

          <div className="flex justify-between items-center pt-6">
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>BACK</span>
            </Button>

            <Button
              size="lg"
              // BUSINESS_INFO is a typed-input step: Continue stays enabled and tells on click
              // (#5857). Later selection/approval steps keep their validity gate.
              disabled={
                (currentStep !== BRAND_CREATION_STEP.BUSINESS_INFO && !stepValidation[currentStep]) ||
                createBusinessMutation.isPending ||
                updateBusinessMutation.isPending ||
                generateBrandIntelMutation.isPending
              }
              onClick={handleNext}
              className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
            >
              <span>{(isManualEntry && currentStep === BRAND_CREATION_STEP.ADJUST_BUSINESS_DESCRIPTION) ? 'SAVE' : (currentStep === BRAND_CREATION_STEP.SET_BUDGET || currentStep === BRAND_CREATION_STEP.MAIN_BUSINESS_PRODUCTS) ? 'CREATE BUSINESS' : 'CONTINUE'}</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
